// 攝影機 / 通道的在線狀態：影像牆格子與左樹用同一個函式（SPEC LIVE-01「狀態同一個來源」；第二輪 B2-20）。
//   - 通道：parent（NVR / Kinio Station）離線 = 設備離線；parent 在線但通道離線（façade presence 或串流回 CHANNEL_OFFLINE / DEVICE_OFFLINE）= 通道離線
//   - 獨立攝影機：離線 = 設備離線
// 格子串流回的離線碼也記在這裡（liveStatus），左樹讀同一份：牆上寫離線時樹上不會是綠點。
import { useSyncExternalStore } from 'react';
import type { Presence } from './devices';

export type CamState = 'online' | 'channel_offline' | 'device_offline' | 'unknown';

const OFFLINE_ERRS = new Set(['CHANNEL_OFFLINE', 'DEVICE_OFFLINE']);

export function camState(cam: { presence: Presence; parentAid: string | null }, parentPresence?: Presence, err?: string | null, liveOffline = false): CamState {
  const offErr = (!!err && OFFLINE_ERRS.has(err)) || liveOffline;
  if (cam.parentAid) {
    if (parentPresence === 'offline') return 'device_offline';
    if (cam.presence === 'offline' || offErr) return 'channel_offline';
    return cam.presence === 'online' || parentPresence === 'online' ? 'online' : 'unknown';
  }
  // 設備本身在線（presence = online）時，串流回的 DEVICE_OFFLINE 只代表這次連不上畫面，不寫「設備離線」（LIVE-01「左樹是綠點時格子不會寫離線」；
  // 第十輪 B10-08：LS-2605 連線逾時後寫「設備離線」，總覽同時算它在線）。presence 不知道時才用串流的判斷（B2-20）。
  if (cam.presence === 'offline' || (cam.presence !== 'online' && ((!!err && err === 'DEVICE_OFFLINE') || liveOffline))) return 'device_offline';
  if (err === 'CHANNEL_OFFLINE') return 'channel_offline';
  return cam.presence === 'online' ? 'online' : 'unknown';
}

/** 狀態點的 class（online / offline / unknown）。 */
export function camDot(s: CamState): 'online' | 'offline' | 'unknown' {
  return s === 'online' ? 'online' : s === 'unknown' ? 'unknown' : 'offline';
}

// ---- 格子串流回報的離線（key = <home>/<device_ref>） ----
const offline = new Set<string>();
const subs = new Set<() => void>();
let version = 0;

export function reportLiveOffline(key: string, isOffline: boolean): void {
  if (offline.has(key) === isOffline) return;
  if (isOffline) offline.add(key);
  else offline.delete(key);
  version++;
  for (const f of subs) f();
}

export function isLiveOffline(key: string): boolean {
  return offline.has(key);
}

/** 訂閱（左樹用）：任何一格的離線回報變了就重畫。 */
export function useLiveOfflineVersion(): number {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => version,
    () => version,
  );
}

// ---- 格子算出來的狀態（key = <home>/<device_ref>） ----
// 切範圍後牆上保留的格子不在目前範圍的設備清單裡（左樹只能退回「我的設備」的 online 旗標），
// 所以格子把自己算出的狀態登記在這裡，左樹先讀這份：同一個通道不會樹上綠點、牆上紅字（VMS UX 評審第三輪 B3-07）。
const tileStates = new Map<string, CamState>();

export function reportTileState(key: string, st: CamState | null): void {
  if ((tileStates.get(key) ?? null) === st) return;
  if (st) tileStates.set(key, st);
  else tileStates.delete(key);
  version++;
  for (const f of subs) f();
}

export function tileState(key: string): CamState | undefined {
  return tileStates.get(key);
}

/** 測試用 */
export function resetLiveStatus(): void {
  offline.clear();
  tileStates.clear();
  version++;
}
